import { Component, OnInit, Output, EventEmitter, OnDestroy, ViewChild, ElementRef, TemplateRef } from '@angular/core';
import { SharedService } from '../../services/shared.service';
import { VennHealthDataService } from '../../services/vennhealth-data.service';
import { Subscription } from 'rxjs';
import { DialogService, DialogCloseResult } from '@progress/kendo-angular-dialog';
import { VennHealthDialogService } from '../../services/vennhealth-dialog.service';
import { SavedQueriesModel } from '../../models/filter-model';
import { environment } from '../../../environments/environment';
import { IndicationGroup } from '@app/models/indication';
import { VennHealthUtils } from '@app/shared/vennHealthUtils';
import { SelectionsErrorModel } from '@app/models/selections-model';
import { UTrackUIEvent } from '@app/models/utrack-ui-event';
import { AccountService } from '@app/services/account-service';

@Component({
  selector: 'app-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.css']
})
export class ToolbarComponent implements OnInit, OnDestroy {

  @ViewChild('queryNameTextbox', {static: false}) queryNameTextboxInputRef: ElementRef;
  @Output() runQuery = new EventEmitter();
  @Output() loadQuery = new EventEmitter();

  unsavedQueryName = '';
  queryName = this.unsavedQueryName;
  queryId = 0;
  savedFilterNamesList = [];
  unsavedChanges = false;
  currentSavedFilters: SavedQueriesModel;
  enableToolbarButtons: boolean = false;
  validationErrors: SelectionsErrorModel[] = [];
  allIndications: IndicationGroup[] = [];
  selectedDataSets: any[] = [];

  validationErrorsSubscription: Subscription;
  savedFilterNamesListSubscription: Subscription;

  isDemoUser = false;

  constructor(
    private readonly sharedService: SharedService,
    private readonly vennHealthDataService: VennHealthDataService,
    private readonly kendoDialogService: DialogService,
    private readonly vennHealthDialogService: VennHealthDialogService,
    private readonly accountService: AccountService
  ) { }

  ngOnInit(): void {
    const account = this.accountService.accountValue;
    if (account) {
      this.isDemoUser = account.roles.includes(103);
    }

    this.sharedService.allIndications.subscribe(data => {
      if (data) {
        // if (!VennHealthUtils.isEqualsJson(this.allIndications, data)) {
        //   this.checkForQueryChanges();
        // }
        this.allIndications = data;
        const enable = this.groupHasIndicationSelections(data);
        this.enableToolbarButtons = enable;
      }
    });

    // set this here to avoid console errors of filterSelections undefined
    this.currentSavedFilters = {
      queryId: 0,
      queryName: this.unsavedQueryName,
      indicationGroups: null,
      dataSets: null,
      startDate: null
    };

    this.unsavedQueryName = `${environment.defaultQueryName} ${new Date(Date.now()).toLocaleDateString()}`;
    this.queryName = this.unsavedQueryName;
    this.sharedService.setActiveQueryName(this.queryName);

    this.validationErrorsSubscription = this.sharedService.validationErrors.subscribe(
      data => {
        if (data !== null) {
          this.validationErrors = data;
        }
      }
    );

    this.savedFilterNamesListSubscription = this.vennHealthDataService.getClientSavedFilterNamesList().subscribe(
      data => {
        if (data !== null) {
          this.savedFilterNamesList = data;
        }
      }
    );

    this.sharedService.activeQueryName.subscribe(
      data => {
        if (data !== null) {
          this.queryName = data;
        }
      }
    );

    this.sharedService.activeQueryId.subscribe(
      data => {
        if (data !== null) {
          this.queryId = data;
        }
      }
    );

    this.sharedService.activeSavedFilters.subscribe(
      data => {
        if (data !== null) {
          // use JSON.parse to assign a detached copy of the saved filters
          this.currentSavedFilters = JSON.parse(JSON.stringify(data));
        }
      }
    );

    this.sharedService.selectedDataSets.subscribe(
      data => {
        if (data) {
          this.selectedDataSets = data;
          this.checkForQueryChanges();
        }
      }
    );
  }

  groupHasIndicationSelections(indicationGroups: IndicationGroup[]): boolean {
    for (let i = 0; i < indicationGroups.length; i++) {
      const group = indicationGroups[i];
      if (group.indications.some(i => i.selected)) {
        return true;
      }
    }
    return false;
  }

  disableRunQueryButton(): boolean {
    if ((this.validationErrors && this.validationErrors.length > 0) || 
    this.hasReachedDemoSelectionLimit() || this.hasReachedSelectionLimit()) {
      return true;
    } else {
      return false;
    }
  }

  runQueryClick() {
    this.sharedService.setRunQuery();
    this.scrollToResults();
  }

  saveQueryClick(template: TemplateRef<any>) {
    this.showSaveDialog(template);
  }

  resetQueryClick() {
    const e: UTrackUIEvent = {
      details: 'Reset',
      additionalDetails: ''
    };
    this.vennHealthDataService.utrackEvent(e).subscribe();

    this.sharedService.setResetQuery();
    this.sharedService.setcloseAllPanels();
  }

  scrollToResults(): void {
    document.getElementById("runQueryScrollToDiv").scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest"
    });
  }

  public showSaveDialog(template: TemplateRef<any>) {

    // populate existing query name if applicable
    setTimeout(() => {
      if (this.queryName !== this.unsavedQueryName) {
        this.queryNameTextboxInputRef.nativeElement.value = this.queryName;
        this.queryNameTextboxInputRef.nativeElement.select();
      }
    }, 10);

    const dialog = this.kendoDialogService.open({
        title: 'Save Query',
        content: template,
        // width: '25vw',
        // height: '30vh',
        width: 450,
        height: 250,
        minWidth: 250,
        actions: [
            { text: 'Cancel' },
            { text: 'Save', primary: true, themeColor: 'primary' }
        ]
    });

    dialog.result.subscribe((result) => {
      if (result instanceof DialogCloseResult) {
          console.log('close');
      } else {
          console.log('action', result);
          if (result.text === 'Save') {
            const textboxValue = this.queryNameTextboxInputRef.nativeElement.value.trim();

            //validate 
            if (!textboxValue || textboxValue.length < 3) {
              this.vennHealthDialogService.showNotification("Query Name must be at least 3 characters long.", {style: 'warning', icon: true });
              return;
            }
            const regex = /^[ A-Za-z0-9_@./#&+-]*$/;
            if (!regex.test(textboxValue)) {
              this.vennHealthDialogService.showNotification("Query Name may only contain letters, numbers, and the following special characters _@./#&+-.", {style: 'warning', icon: true });
              return;
            }

            // check for same name overwrite
            if (textboxValue === this.queryName.trim()) {
              // overwrite confirm dialog
              const confirmDialog = this.kendoDialogService.open({
                title: 'Confirm Overwrite',
                content: `Saving will overwrite the existing query named "${this.queryName}". Proceed?`,
                width: 450,
                height: 250,
                minWidth: 250,
                actions: [{ text: 'No' }, { text: 'Yes', primary: true, themeColor: 'primary' }]
              });
              confirmDialog.result.subscribe(confirmResult => {
                if (confirmResult instanceof DialogCloseResult) {
                  console.log('close');
                } else {
                  if (confirmResult.text === 'Yes') {
                    this.saveQuery(textboxValue);
                  }
                }
              });
            } else { // new query save
              this.saveQuery(textboxValue);
            }
          }
      }
    });
  }

  saveQuery(textboxValue: string) {
    const filters: SavedQueriesModel = {
      indicationGroups: this.allIndications,
      queryName: textboxValue,
      queryId: this.queryId,
      dataSets: this.selectedDataSets,
      startDate: new Date()
    };
    this.vennHealthDataService
      .saveFilters(filters)
      .subscribe({
        next: (response) => {
          if (response) {
            this.vennHealthDialogService.showNotification(`Query filters for ${textboxValue} were successfully saved!`);
            this.queryName = textboxValue;
            this.sharedService.setActiveQueryName(this.queryName);
            this.sharedService.setActiveQueryId(response.queryId);
            this.sharedService.setRefreshSavedQueries();
          }
        },
        error: (e) => {
          this.vennHealthDialogService.showNotification(e, {style: 'error', icon: true });
        },
        complete: () => {
        }
    });
  }

  checkForQueryChanges() {
    let indicationsChanged = false;
    let dataSetsChanged = false;
    if (
      this.allIndications !== null &&
      this.currentSavedFilters.indicationGroups !== null
    ) {
      const indicationsSelectionsChanged = !VennHealthUtils.isEqualsJson(this.allIndications, this.currentSavedFilters.indicationGroups);
      indicationsChanged = indicationsSelectionsChanged &&
      this.currentSavedFilters.queryName ===
      this.queryName;
    }

    if (
      this.selectedDataSets !== null &&
      this.currentSavedFilters.dataSets !== null
    ) {
      const dataSetSelectionsChanged = !VennHealthUtils.isEqualsJson(this.selectedDataSets, this.currentSavedFilters.dataSets);
      dataSetsChanged = dataSetSelectionsChanged &&
      this.currentSavedFilters.queryName ===
      this.queryName;
    }

    if (indicationsChanged || dataSetsChanged) {
      this.queryName = this.unsavedQueryName;
      this.sharedService.setActiveQueryName(this.queryName);
      this.unsavedChanges = true;
    } else {
      this.unsavedChanges = false;
    }
  }

  hasReachedDemoSelectionLimit(): boolean {
    const selectedIndicationsCount = this.getSelectedIndicationsCount();
    if (this.isDemoUser && selectedIndicationsCount > 2) {
      return true;
    }
    return false;
  }

  hasReachedSelectionLimit(): boolean {
    const selectedIndicationsCount = this.getSelectedIndicationsCount();
    if (selectedIndicationsCount > 5) {
      return true;
    }
    return false;
  }

  getSelectedIndicationsCount(): number {
    var count = 0;
    this.allIndications.forEach(group => {
      group.indications.forEach(indication => {
        if (indication.selected) {
          count++;
        }
      });
    })

    return count;
  }

  ngOnDestroy(): void {
    this.savedFilterNamesListSubscription.unsubscribe();
    this.validationErrorsSubscription.unsubscribe();
  }
}
